<template>
  <div class="overlay" v-if="generalStore.sidebarOpen" @click="generalStore.toggleSidebar"></div>
  <aside :class="{ open: generalStore.sidebarOpen, small: generalStore.sidebarSmall }" class="sidebar">
    <div class="handle" @click="generalStore.toggleSidebarSmall"></div>
    <NiceButton class="mobile-toggle" icon="icon-menu" @click="generalStore.toggleSidebar" />

    <!-- App switcher -->
    <AppSwitcher />

    <!-- Switcher -->
    <SidebarSwitcher />

    <!-- Navigation -->
    <div class="sidebar-items">
      <div class="sidebar-item-wrapper" v-for="link in links" :key="link.path">
        <div class="sidebar-item-title" v-if="!(link.hidden && link.hidden()) && link.type == 'TITLE'">{{ link.name }}</div>
        <component
          :is="link.disabled && link.disabled() ? 'div' : 'router-link'"
          class="sidebar-item"
          :class="{ disabled: link.disabled && link.disabled() }"
          v-if="!(link.hidden && link.hidden()) && !link.type"
          :to="{
            name: link.path,
            params: {
              ...link.params,
              organizationId: generalStore.organizationId,
              eventId: event ? event.id : undefined,
            },
            query: {
              ...link.query,
            },
          }"
          @click="generalStore.setSidebarOpen(false)"
        >
          <NiceIcon :icon="link.icon" />
          <span>{{ link.name }}</span>
        </component>
      </div>
    </div>

    <!-- Footer -->
    <div class="sidebar-footer">
      <User :small="generalStore.sidebarSmall" />
    </div>
  </aside>
</template>

<script setup>
import { computed } from "vue";

import User from "@/components/User.vue";
import SidebarSwitcher from "@/components/SidebarSwitcher.vue";
import AppSwitcher from "@/components/AppSwitcher.vue";
import CommonService from "@/services/common.service";

import { useGeneralStore } from "@/store/general.store";
const generalStore = useGeneralStore();
const organization = computed(() => generalStore.organization);
const event = computed(() => generalStore.event);
const modules = computed(() => generalStore.modules);
const permissions = computed(() => generalStore.permissions);

import { useGettext } from "vue3-gettext";
const { $gettext } = useGettext();

const links = computed(() => [
  // Events
  {
    type: "TITLE",
    name: $gettext("Event"),
    hidden: () => !organization.value || !event.value,
  },
  {
    name: CommonService.routes.DASHBOARD.title,
    path: CommonService.routes.DASHBOARD.path,
    icon: CommonService.routes.DASHBOARD.icon,
    hidden: () => !organization.value || !event.value,
  },
  {
    name: CommonService.routes.GROUPS.title,
    path: CommonService.routes.GROUPS.path,
    icon: CommonService.routes.GROUPS.icon,
    hidden: () => !organization.value || !event.value,
  },
  {
    name: CommonService.routes.PARTNERS.title,
    path: CommonService.routes.PARTNERS.path,
    icon: CommonService.routes.PARTNERS.icon,
    hidden: () => !organization.value || !event.value,
    disabled: () => !permissions.value.can_view_partners || !modules.value.PARTNERS,
  },
  {
    name: CommonService.routes.GUESTS.title,
    path: CommonService.routes.GUESTS.path,
    icon: CommonService.routes.GUESTS.icon,
    hidden: () => !organization.value || !event.value,
  },
  {
    name: CommonService.routes.CHECKIN_LOGS.title,
    path: CommonService.routes.CHECKIN_LOGS.path,
    icon: CommonService.routes.CHECKIN_LOGS.icon,
    hidden: () => !organization.value || !event.value,
  },
  {
    name: CommonService.routes.CHECKIN.title,
    path: CommonService.routes.CHECKIN.path,
    icon: CommonService.routes.CHECKIN.icon,
    query: {
      is_active: true,
      // is_checked_in: false,
    },
    hidden: () => !organization.value || !event.value,
  },
  // Organizations
  {
    type: "TITLE",
    name: $gettext("Organization"),
  },
  {
    name: CommonService.routes.ORGANIZATIONS.title,
    path: CommonService.routes.ORGANIZATIONS.path,
    icon: CommonService.routes.ORGANIZATIONS.icon,
  },
  {
    name: CommonService.routes.EVENTS.title,
    path: CommonService.routes.EVENTS.path,
    icon: CommonService.routes.EVENTS.icon,
    hidden: () => !organization.value,
  },
  {
    name: CommonService.routes.USERS.title,
    path: CommonService.routes.USERS.path,
    icon: CommonService.routes.USERS.icon,
    hidden: () => !organization.value,
    disabled: () => !permissions.value.can_view_users,
  },
  {
    name: CommonService.routes.SANDBOX.title,
    path: CommonService.routes.SANDBOX.path,
    icon: CommonService.routes.SANDBOX.icon,
    hidden: () => location.hostname != "test.olaii.com" || !organization.value,
  },
]);
</script>

<style scoped lang="scss">
.overlay {
  display: none;
  z-index: 99;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
}

.sidebar {
  display: flex;
  flex-direction: column;
  position: relative;
  flex-shrink: 0;
  border-right: 1px solid var(--border-color);
  z-index: 1000;

  height: 100%;
  // height: -moz-available;
  // height: -webkit-fill-available;
  // height: fill-available;
  background: var(--card-bg);
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.03);

  --sidebar-width: 300px;
  width: var(--sidebar-width);

  .mobile-toggle {
    display: none;
    position: absolute;
    bottom: 1rem;
    left: 100%;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 2px;
    height: 100%;
    background: transparent;
    cursor: w-resize;

    &:hover {
      background: var(--primary-color);
    }
  }

  &.small {
    --sidebar-width: 65px;

    .handle {
      cursor: e-resize;
    }

    .sidebar-logo {
      padding: 10px;

      img {
        height: auto;
      }

      .nice-icon {
        width: 100%;
      }
    }

    .sidebar-items {
      .sidebar-item-title {
        display: none;
      }

      .sidebar-item {
        padding: 8px;
        justify-content: center;
        position: relative;

        .nice-icon {
          margin: 0;
        }

        span {
          display: none;
          position: absolute;
          top: 0;
          left: 100%;
          padding: 8px;
          background: var(--primary-color-bg);
        }

        &:hover {
          span {
            // display: block;
          }
        }
      }
    }
  }

  .sidebar-logo {
    flex-shrink: 0;
    padding: 20px;
    padding-bottom: 0px;
    padding-top: 20px;
    color: var(--primary-color);
    text-align: center;

    img {
      height: 80px;
      max-width: 100%;
    }

    .nice-icon {
      height: 40px;
      width: 60px;
    }
  }

  .sidebar-items {
    padding: 1rem;
    flex-grow: 1;
    overflow: auto;

    .sidebar-item-wrapper {
      &:first-child {
        .sidebar-item-title {
          padding-top: 0;
        }
      }
    }

    .sidebar-item-title {
      font-size: 0.8em;
      font-weight: bold;
      text-transform: uppercase;
      padding-left: 15px;
      padding-right: 15px;
      padding-bottom: 0.2rem;
      padding-top: 0.5rem;
    }

    .sidebar-item {
      padding: 8px 15px;
      border-radius: var(--border-radius);
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      text-decoration: none;
      color: var(--font-color);
      // font-weight: 600;

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      svg {
        height: 23px;
        width: 23px;
        margin-right: 1rem;
      }

      &:hover {
        // background: var(--primary-color-bg);
        background: linear-gradient(90deg, var(--primary-color-bg), transparent);
      }

      &.active,
      &.router-link-exact-active {
        background: var(--primary-color-bg);
        color: var(--primary-color);
      }
    }
  }

  .sidebar-footer {
    flex-shrink: 0;
  }
}

// Mobile
@media (max-width: 576px) {
  .overlay {
    display: block !important;
  }

  aside {
    position: absolute !important;
    top: 0;
    left: calc(-1 * var(--sidebar-width));
    z-index: 100;

    transition: all 0.3s;

    &.open {
      left: 0px;
    }

    .mobile-toggle {
      display: block !important;
    }

    .toggle {
      display: flex;
    }

    .mobile {
      display: flex;
    }

    .items {
      position: fixed;
      background: var(--sidebar-bg);
    }
  }
}
</style>
